import { Component, type ErrorInfo, type ReactNode } from 'react';
import { recoverIfStale } from '../lib/staleBuild';

/**
 * Last line of defence for a crash anywhere in the app. Without it a single
 * render error leaves a blank white page.
 *
 *   1. Most crashes right after a deploy are an old cached build meeting the new
 *      server — that heals itself: newer build found → caches cleared → reload.
 *   2. Anything else shows a plain message with a Reload button instead of a
 *      blank screen, and the error stays in the console for us.
 */
interface State { error: Error | null; checking: boolean }

export default class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, checking: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, checking: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('App crashed:', error, info.componentStack);
    recoverIfStale().then((reloading) => { if (!reloading) this.setState({ checking: false }); });
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.state.checking) {
      return <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>Updating to the latest version…</div>;
    }
    return (
      <div style={{ maxWidth: 520, margin: '60px auto', padding: 24, border: '1px solid #e2e8f0', borderRadius: 12, fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Something went wrong on this page</div>
        <div style={{ marginTop: 6, fontSize: 13, color: '#475569' }}>
          Reload to try again. If it keeps happening, send a screenshot of this message to the team.
        </div>
        <div style={{ marginTop: 10, fontFamily: 'ui-monospace, monospace', fontSize: 11.5, color: '#b91c1c', wordBreak: 'break-word' }}>
          {String(this.state.error?.message || this.state.error)}
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ marginTop: 14, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0d9488', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
        >
          Reload
        </button>
      </div>
    );
  }
}
